import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { X, Loader2, Image as ImageIcon, CloudOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BunnyLibraryModal - Image library browser with dual-source support
 * 
 * Features:
 * - Fetches images from Bunny.net CDN (primary)
 * - Automatically falls back to Emergent Lab CDN if Bunny is empty
 * - Shows source indicator (Bunny/Emergent)
 * - Responsive grid layout
 * - Image selection callback
 * 
 * @param {boolean} isOpen - Modal visibility state
 * @param {function} onClose - Close modal callback
 * @param {function} onSelect - Image selection callback (receives URL)
 * @param {string} folder - Folder to browse (e.g., 'hero', 'projects')
 */
const BunnyLibraryModal = ({ isOpen, onClose, onSelect, folder = 'images' }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState([]);
  const [source, setSource] = useState('bunny');
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, folder]);

  const loadImages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/images/library`, {
        params: { folder }
      });

      if (response.data.success) {
        setImages(response.data.images || []);
        setSource(response.data.source || 'bunny');
      }
    } catch (error) {
      console.error('Failed to load library:', error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar biblioteca",
        description: "Não foi possível carregar as imagens. Tente novamente."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = () => {
    if (!selectedImage) {
      toast({
        variant: "destructive",
        title: "Nenhuma imagem selecionada",
        description: "Por favor, selecione uma imagem primeiro"
      });
      return;
    }

    onSelect(selectedImage.url);
    toast({
      title: "Imagem selecionada!",
      description: `Imagem adicionada do ${source === 'bunny' ? 'Bunny CDN' : 'Emergent Lab'}`
    });
    onClose();
    setSelectedImage(null);
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-[#2B2D42]">Biblioteca de Imagens</h2>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-gray-600">
                Pasta: <span className="font-medium">{folder}</span>
              </p>
              <span className="text-gray-400">•</span>
              <div className="flex items-center gap-1">
                {source === 'bunny' ? (
                  <>
                    <CloudOff className="w-4 h-4 text-[#00B4D8]" />
                    <span className="text-xs font-medium text-[#00B4D8]">Bunny CDN</span>
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-4 h-4 text-orange-500" />
                    <span className="text-xs font-medium text-orange-500">Emergent Lab</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#00B4D8] mb-4" />
              <p className="text-gray-600">A carregar imagens...</p>
            </div>
          ) : images.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <ImageIcon className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-600 text-lg font-medium">Nenhuma imagem disponível</p>
              <p className="text-sm text-gray-500 mt-2">
                Faça upload de imagens para esta pasta ou use sugestões
              </p>
            </div>
          ) : (
            <>
              {source === 'emergent' && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800">
                    ℹ️ <strong>Bunny CDN vazio.</strong> A mostrar sugestões do Emergent Lab. 
                    Faça upload de imagens para usar o Bunny CDN.
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map((image) => (
                  <div
                    key={image.id}
                    onClick={() => handleImageClick(image)}
                    className={`
                      relative group cursor-pointer rounded-lg overflow-hidden border-2 
                      transition-all duration-200 hover:shadow-lg
                      ${selectedImage?.id === image.id 
                        ? 'border-[#00B4D8] shadow-lg' 
                        : 'border-gray-200 hover:border-[#00B4D8]'
                      }
                    `}
                  >
                    <div className="aspect-square bg-gray-100 relative">
                      <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {selectedImage?.id === image.id && (
                        <div className="absolute inset-0 bg-[#00B4D8] bg-opacity-20 flex items-center justify-center">
                          <div className="bg-[#00B4D8] text-white rounded-full p-2">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-2 bg-white">
                      <p className="text-xs text-gray-600 truncate" title={image.name}>
                        {image.name}
                      </p>
                      {image.size > 0 && (
                        <p className="text-xs text-gray-400">
                          {(image.size / 1024).toFixed(1)} KB
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {images.length > 0 ? (
              <>
                <strong>{images.length}</strong> imagem{images.length !== 1 ? 's' : ''} disponível
                {images.length !== 1 ? 'eis' : ''}
              </>
            ) : (
              'Nenhuma imagem disponível'
            )}
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSelect}
              disabled={!selectedImage}
              className="bg-[#00B4D8] hover:bg-[#48CAE4]"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Selecionar Imagem
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BunnyLibraryModal;
